import { type ChainedCommands, type Editor, useEditorState } from '@tiptap/react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import { useCallback } from 'react';

import type { CustomIconButtonProps } from '@ui/components/buttons/customIconButton';
import {
  insertTemplateHorizontalRule,
  isTemplateAlignmentActive,
  toggleTemplateAlignment,
} from '@ui/components/editor/richTextEditorCommands';
import { HEADING_NODE_NAME, PARAGRAPH_NODE_NAME } from '@ui/components/editor/richTextUtils';
import { LINK_MARK_NAME } from '@ui/components/editor/templateLinkUtils';
import {
  TABLE_NODE_NAME,
  TEMPLATE_BLOCK_TYPES,
  TEMPLATE_FONT_SIZES,
  type TemplateBlockType,
  type TemplateTextAlignment,
} from '@ui/components/editor/templateTextUtils';
import {
  AlignCenterIcon,
  AlignJustifyIcon,
  AlignLeftIcon,
  AlignRightIcon,
  BlockquoteIcon,
  BoldIcon,
  BulletListIcon,
  CodeBlockIcon,
  Heading2Icon,
  Heading3Icon,
  IndentDecreaseIcon,
  IndentIncreaseIcon,
  ItalicIcon,
  OrderedListIcon,
  RedoIcon,
  RemoveFormattingIcon,
  SeparatorHorizontalIcon,
  StrikethroughIcon,
  SubscriptIcon,
  SuperscriptIcon,
  UnderlineIcon,
  UndoIcon,
} from '@ui/components/icons';
import type { ActionsMenuAction } from '@ui/components/menus/actionsMenu';

const TEXT_STYLE_MARK = 'textStyle';
const DEFAULT_FONT_SIZE = 'default';

const HEADING_LEVELS: Record<TemplateBlockType, 1 | 2 | 3 | null> = {
  paragraph: null,
  heading1: 1,
  heading2: 2,
  heading3: 3,
};

export const NEW_TABLE_SIZE = { rows: 3, cols: 3, withHeaderRow: true };

export type RichTextEditorMode = 'rich' | 'template';

type ToolbarIcon = CustomIconButtonProps['icon'];

type ToolbarCommand = (chain: ChainedCommands) => ChainedCommands;

export interface ToolbarItem {
  messageId: string;
  icon: ToolbarIcon;
  isActive?: (editor: Editor) => boolean;
  isDisabled?: (editor: Editor) => boolean;
  run: (editor: Editor) => void;
}

interface ToolbarItemState {
  active: boolean;
  disabled: boolean;
}

export interface ToolbarChoice {
  value: string;
  label: string;
}

export interface ToolbarMenuOptions {
  label: string;
  currentLabel: string;
  actions: ActionsMenuAction[];
  className: string;
}

export interface ToolbarState {
  items: Record<string, ToolbarItemState>;
  blockType: TemplateBlockType;
  fontSize: string | null;
  color: string | null;
  backgroundColor: string | null;
  linkActive: boolean;
  insideTable: boolean;
  canMergeCells: boolean;
  canSplitCell: boolean;
}

const toggleItem = (
  messageId: string,
  icon: ToolbarIcon,
  name: string,
  toggle: ToolbarCommand,
  attributes?: Record<string, number>
): ToolbarItem => ({
  messageId,
  icon,
  isActive: (editor) => editor.isActive(name, attributes),
  run: (editor) => toggle(editor.chain().focus()).run(),
});

const alignItem = (messageId: string, icon: ToolbarIcon, alignment: TemplateTextAlignment): ToolbarItem => ({
  messageId,
  icon,
  isActive: (editor) => editor.isActive({ textAlign: alignment }),
  run: (editor) => editor.chain().focus().toggleTextAlign(alignment).run(),
});

const toTemplateAlignItem = (item: ToolbarItem, alignment: TemplateTextAlignment): ToolbarItem => ({
  ...item,
  isActive: (editor) => isTemplateAlignmentActive(editor, alignment),
  run: (editor) => toggleTemplateAlignment(editor, alignment),
});

const UNDO_ITEM: ToolbarItem = {
  messageId: 'richTextEditor.undo',
  icon: UndoIcon,
  isDisabled: (editor) => !editor.can().undo(),
  run: (editor) => editor.chain().focus().undo().run(),
};

const REDO_ITEM: ToolbarItem = {
  messageId: 'richTextEditor.redo',
  icon: RedoIcon,
  isDisabled: (editor) => !editor.can().redo(),
  run: (editor) => editor.chain().focus().redo().run(),
};

const CLEAR_FORMATTING_ITEM: ToolbarItem = {
  messageId: 'richTextEditor.clearFormatting',
  icon: RemoveFormattingIcon,
  run: (editor) => editor.chain().focus().unsetAllMarks().clearNodes().run(),
};

const HORIZONTAL_RULE_ITEM: ToolbarItem = {
  messageId: 'richTextEditor.horizontalRule',
  icon: SeparatorHorizontalIcon,
  run: (editor) => insertTemplateHorizontalRule(editor),
};

const DECREASE_INDENT_ITEM: ToolbarItem = {
  messageId: 'richTextEditor.decreaseIndent',
  icon: IndentDecreaseIcon,
  isDisabled: (editor) => !editor.can().outdentTemplateBlocks(),
  run: (editor) => editor.chain().focus().outdentTemplateBlocks().run(),
};

const INCREASE_INDENT_ITEM: ToolbarItem = {
  messageId: 'richTextEditor.increaseIndent',
  icon: IndentIncreaseIcon,
  isDisabled: (editor) => !editor.can().indentTemplateBlocks(),
  run: (editor) => editor.chain().focus().indentTemplateBlocks().run(),
};

const BOLD_ITEM = toggleItem('richTextEditor.bold', BoldIcon, 'bold', (chain) => chain.toggleBold());
const ITALIC_ITEM = toggleItem('richTextEditor.italic', ItalicIcon, 'italic', (chain) => chain.toggleItalic());
const UNDERLINE_ITEM = toggleItem('richTextEditor.underline', UnderlineIcon, 'underline', (chain) =>
  chain.toggleUnderline()
);
const STRIKETHROUGH_ITEM = toggleItem('richTextEditor.strikethrough', StrikethroughIcon, 'strike', (chain) =>
  chain.toggleStrike()
);
const SUBSCRIPT_ITEM = toggleItem('richTextEditor.subscript', SubscriptIcon, 'subscript', (chain) =>
  chain.toggleSubscript()
);
const SUPERSCRIPT_ITEM = toggleItem('richTextEditor.superscript', SuperscriptIcon, 'superscript', (chain) =>
  chain.toggleSuperscript()
);
const HEADING_2_ITEM = toggleItem(
  'richTextEditor.heading2',
  Heading2Icon,
  HEADING_NODE_NAME,
  (chain) => chain.toggleHeading({ level: 2 }),
  { level: 2 }
);
const HEADING_3_ITEM = toggleItem(
  'richTextEditor.heading3',
  Heading3Icon,
  HEADING_NODE_NAME,
  (chain) => chain.toggleHeading({ level: 3 }),
  { level: 3 }
);
const BULLET_LIST_ITEM = toggleItem('richTextEditor.bulletList', BulletListIcon, 'bulletList', (chain) =>
  chain.toggleBulletList()
);
const ORDERED_LIST_ITEM = toggleItem('richTextEditor.orderedList', OrderedListIcon, 'orderedList', (chain) =>
  chain.toggleOrderedList()
);
const BLOCKQUOTE_ITEM = toggleItem('richTextEditor.blockquote', BlockquoteIcon, 'blockquote', (chain) =>
  chain.toggleBlockquote()
);
const CODE_BLOCK_ITEM = toggleItem('richTextEditor.codeBlock', CodeBlockIcon, 'codeBlock', (chain) =>
  chain.toggleCodeBlock()
);
const ALIGN_LEFT_ITEM = alignItem('richTextEditor.alignLeft', AlignLeftIcon, 'left');
const ALIGN_CENTER_ITEM = alignItem('richTextEditor.alignCenter', AlignCenterIcon, 'center');
const ALIGN_RIGHT_ITEM = alignItem('richTextEditor.alignRight', AlignRightIcon, 'right');
const ALIGN_JUSTIFY_ITEM = alignItem('richTextEditor.alignJustify', AlignJustifyIcon, 'justify');

export const HISTORY_ITEMS: ToolbarItem[] = [UNDO_ITEM, REDO_ITEM];

export const FORMAT_ITEMS: ToolbarItem[] = [
  BOLD_ITEM,
  ITALIC_ITEM,
  STRIKETHROUGH_ITEM,
  HEADING_2_ITEM,
  HEADING_3_ITEM,
  BULLET_LIST_ITEM,
  ORDERED_LIST_ITEM,
  BLOCKQUOTE_ITEM,
  CODE_BLOCK_ITEM,
  ALIGN_LEFT_ITEM,
  ALIGN_CENTER_ITEM,
  ALIGN_RIGHT_ITEM,
];

export const TEMPLATE_MARK_ITEMS: ToolbarItem[] = [
  BOLD_ITEM,
  ITALIC_ITEM,
  UNDERLINE_ITEM,
  STRIKETHROUGH_ITEM,
  SUBSCRIPT_ITEM,
  SUPERSCRIPT_ITEM,
];

export const TEMPLATE_CLEAR_ITEMS: ToolbarItem[] = [CLEAR_FORMATTING_ITEM];

export const TEMPLATE_BLOCK_ITEMS: ToolbarItem[] = [
  BULLET_LIST_ITEM,
  ORDERED_LIST_ITEM,
  DECREASE_INDENT_ITEM,
  INCREASE_INDENT_ITEM,
  BLOCKQUOTE_ITEM,
  HORIZONTAL_RULE_ITEM,
];

export const TEMPLATE_ALIGN_ITEMS: ToolbarItem[] = [
  toTemplateAlignItem(ALIGN_LEFT_ITEM, 'left'),
  toTemplateAlignItem(ALIGN_CENTER_ITEM, 'center'),
  toTemplateAlignItem(ALIGN_RIGHT_ITEM, 'right'),
  toTemplateAlignItem(ALIGN_JUSTIFY_ITEM, 'justify'),
];

const TOOLBAR_ITEMS: Record<RichTextEditorMode, ToolbarItem[]> = {
  rich: [...HISTORY_ITEMS, ...FORMAT_ITEMS],
  template: [
    ...HISTORY_ITEMS,
    ...TEMPLATE_MARK_ITEMS,
    ...TEMPLATE_CLEAR_ITEMS,
    ...TEMPLATE_BLOCK_ITEMS,
    ...TEMPLATE_ALIGN_ITEMS,
  ],
};

const readBlockType = (editor: Editor): TemplateBlockType => {
  const level = editor.isActive(HEADING_NODE_NAME) ? editor.getAttributes(HEADING_NODE_NAME).level : null;
  return TEMPLATE_BLOCK_TYPES.find((blockType) => HEADING_LEVELS[blockType] === level) ?? PARAGRAPH_NODE_NAME;
};

export const applyBlockType = (editor: Editor, blockType: TemplateBlockType): void => {
  const level = HEADING_LEVELS[blockType];
  if (isNil(level)) {
    editor.chain().focus().setParagraph().run();
    return;
  }
  editor.chain().focus().setHeading({ level }).run();
};

export const buildFontSizeChoices = (fontSize: string | null, defaultLabel: string): ToolbarChoice[] => [
  { value: DEFAULT_FONT_SIZE, label: defaultLabel },
  ...TEMPLATE_FONT_SIZES.map((size) => ({ value: size, label: size })),
  ...(isNil(fontSize) || TEMPLATE_FONT_SIZES.includes(fontSize) ? [] : [{ value: fontSize, label: fontSize }]),
];

export const applyFontSize = (editor: Editor, fontSize: string): void => {
  if (fontSize === DEFAULT_FONT_SIZE) {
    editor.chain().focus().unsetFontSize().run();
    return;
  }
  editor.chain().focus().setFontSize(fontSize).run();
};

const readTextStyle = (editor: Editor, attribute: string): string | null => {
  const value = editor.getAttributes(TEXT_STYLE_MARK)[attribute];
  return isString(value) && !isEmpty(value) ? value : null;
};

export const readToolbarState = (editor: Editor, mode: RichTextEditorMode): ToolbarState => ({
  items: Object.fromEntries(
    TOOLBAR_ITEMS[mode].map((item) => [
      item.messageId,
      { active: item.isActive?.(editor) ?? false, disabled: item.isDisabled?.(editor) ?? false },
    ])
  ),
  blockType: readBlockType(editor),
  fontSize: readTextStyle(editor, 'fontSize'),
  color: readTextStyle(editor, 'color'),
  backgroundColor: readTextStyle(editor, 'backgroundColor'),
  linkActive: editor.isActive(LINK_MARK_NAME),
  insideTable: editor.isActive(TABLE_NODE_NAME),
  canMergeCells: editor.can().mergeCells(),
  canSplitCell: editor.can().splitCell(),
});

export const useToolbarState = (editor: Editor | null, mode: RichTextEditorMode): ToolbarState | null => {
  const selectToolbarState = useCallback<() => ToolbarState | null>(
    () => (isNil(editor) ? null : readToolbarState(editor, mode)),
    [editor, mode]
  );
  return useEditorState({ editor, selector: selectToolbarState });
};
